import React from 'react';
import PropTypes from 'prop-types';
import { useSortableRow } from '../shared/dndSortable';

const fieldShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  type_name: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  parent_option_field_name: PropTypes.string,
  parent_option_field_value: PropTypes.string,
  parameter_formats: PropTypes.arrayOf(PropTypes.any).isRequired,
  showFieldEditLink: PropTypes.bool,
  edit_path: PropTypes.string,
});

// Presentational card. Shared by the sortable `Field` below and by the
// `DragOverlay` in PfFieldsSelector so the dragged card renders identically in
// both places. Kept dnd-free so it can also be rendered inside the overlay
// (which floats above both panes instead of being clipped by a column's
// `overflow` scroll box).
export const FieldCard = React.forwardRef(function FieldCard({
  field,
  index,
  populateInputs = false,
  error = false,
  showFieldEditLink = false,
  style = undefined,
  dragging = false,
  handleProps = {},
}, ref) {
  const parentFieldNode = () => {
    if (field.parent_option_field_name) {
      return (
        <span>
          When value of
          <span className="text-semibold"> {field.parent_option_field_name} </span>
          is
          <span className="text-semibold"> {field.parent_option_field_value} </span>
        </span>
      );
    }
    return <span className="text-muted">None</span>;
  };

  const parameterFormats = () => {
    const nodes = field.parameter_formats.map((parameterFormat) => (
      <li key={parameterFormat.id}>{parameterFormat.name}</li>
    ));
    if (nodes.length > 0) {
      return (
        <ul className="list-inline list-inline-separate">{nodes}</ul>
      );
    }
    return <span className="text-muted">Unused</span>;
  };

  const inputNodes = () => {
    if (populateInputs) {
      return (
        <>
          <input type="hidden" name="parameter_format[elements_attributes][][elementable_id]" value={field.id} />
          <input type="hidden" name="parameter_format[elements_attributes][][position]" value={index + 1} />
        </>
      );
    }
    return null;
  };

  return (
    <li
      ref={ref}
      style={style}
      className={`field-card panel mb-10 border-${error ? 'danger' : 'default'}${dragging ? ' is-dragging' : ''}`}
      {...handleProps}
    >
      {inputNodes()}
      <div className="panel-body">
        <div className="row">
          <div className="col-sm-6">
            <h6 className="text-semibold no-margin-top">
              {showFieldEditLink ? (
                <a href={field.edit_path} title="Edit">{field.name}</a>
              ) : (
                field.name
              )}
            </h6>
            <span>{field.description}</span>
          </div>
          <div className="col-sm-6">
            <h6 className="text-right no-margin-top">
              <span className={`label label-flat border-${field.color} text-${field.color}`}>
                {field.type_name}
              </span>
            </h6>
          </div>
        </div>
      </div>
      <div className="panel-footer panel-footer-condensed">
        <div className="heading-elements">
          <span className="heading-text">Used in:</span>
          <span className="heading-text ml-10">{parameterFormats()}</span>
          <span className="heading-text pull-right">
            <span>Rules:</span>
            <span className="text-size-small">&nbsp;{parentFieldNode()}</span>
          </span>
        </div>
      </div>
    </li>
  );
});

FieldCard.propTypes = {
  field: fieldShape.isRequired,
  index: PropTypes.number,
  populateInputs: PropTypes.bool,
  error: PropTypes.bool,
  showFieldEditLink: PropTypes.bool,
  style: PropTypes.object,
  dragging: PropTypes.bool,
  handleProps: PropTypes.object,
};

export default function Field({
  field,
  index,
  populateInputs,
  error = false,
  showFieldEditLink = false,
  containerId,
}) {
  const {
    setNodeRef, attributes, listeners, isDragging, style: sortableStyle,
  } = useSortableRow(field.id.toString(), { data: { containerId } });
  // Hide the in-place item while dragging; the floating DragOverlay renders the
  // visible card on top of both panes.
  const style = { ...sortableStyle, opacity: isDragging ? 0 : undefined };

  return (
    <FieldCard
      ref={setNodeRef}
      field={field}
      index={index}
      populateInputs={populateInputs}
      error={error}
      showFieldEditLink={showFieldEditLink}
      style={style}
      handleProps={{ ...attributes, ...listeners }}
    />
  );
}

Field.propTypes = {
  field: fieldShape.isRequired,
  index: PropTypes.number.isRequired,
  populateInputs: PropTypes.bool.isRequired,
  error: PropTypes.bool,
  showFieldEditLink: PropTypes.bool,
  containerId: PropTypes.string.isRequired,
};
